import { API_URL } from "../config/backend";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const API = API_URL;

const STYLES = {
  purple: {
    bg1: "#1a0035", bg2: "#0d0820",
    accent: "#9b5de5", accent2: "#ff3cac",
    glow: "#9b5de5",
    tag_bg: "#2d0050", tag_color: "#c77dff",
  },
  fire: {
    bg1: "#2d0a00", bg2: "#1a0500",
    accent: "#ff6b00", accent2: "#ffe600",
    glow: "#ff6b00",
    tag_bg: "#3d1500", tag_color: "#ff8c00",
  },
  neon: {
    bg1: "#001a1a", bg2: "#000d0d",
    accent: "#00f5ff", accent2: "#3dffa0",
    glow: "#00f5ff",
    tag_bg: "#001f1f", tag_color: "#00f5ff",
  },
  gold: {
    bg1: "#1a1400", bg2: "#0d0a00",
    accent: "#ffe600", accent2: "#ff8c00",
    glow: "#ffe600",
    tag_bg: "#2d2300", tag_color: "#ffe600",
  },
  pink: {
    bg1: "#2d0020", bg2: "#1a0015",
    accent: "#ff3cac", accent2: "#ff8c00",
    glow: "#ff3cac",
    tag_bg: "#3d0030", tag_color: "#ff3cac",
  },
};

// Анимированные частицы на фоне
function Particles({ color }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const particles = Array.from({ length: 18 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 1,
      dx: (Math.random() - 0.5) * 0.4,
      dy: -Math.random() * 0.5 - 0.2,
      o: Math.random() * 0.5 + 0.2,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = color + Math.floor(p.o * 255).toString(16).padStart(2, "0");
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.y < -5) { p.y = canvas.height + 5; p.x = Math.random() * canvas.width; }
        if (p.x < -5) p.x = canvas.width + 5;
        if (p.x > canvas.width + 5) p.x = -5;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [color]);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />;
}

export default function PromoBanner({ compact = false }) {
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    fetch(`${API}/banner`)
      .then(r => r.json())
      .then(d => {
        if (d.banner) {
          setBanner(d.banner);
          setTimeout(() => setVisible(true), 100);
        }
      })
      .catch(() => {});
  }, []);

  if (!banner) return null;

  const s = STYLES[banner.style] || STYLES.purple;

  const handleClick = () => {
    const url = banner.cta_url || "/pro";
    if (url.startsWith("http")) window.open(url, "_blank");
    else navigate(url);
  };

  // ── Компактная версия (для HomePage мобильный) ────────────────────────────
  if (compact) {
    return (
      <div
        onClick={handleClick}
        style={{
          position: "relative", overflow: "hidden",
          background: `linear-gradient(135deg, ${s.bg1} 0%, ${s.bg2} 100%)`,
          border: `2px solid ${s.accent}55`,
          borderRadius: "1.25rem",
          padding: "1rem 1.1rem",
          cursor: "pointer",
          marginBottom: "1rem",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 0.5s ease, transform 0.5s ease, border-color 0.2s, box-shadow 0.2s",
          boxShadow: hovered ? `0 8px 40px ${s.glow}30` : `0 4px 20px ${s.glow}15`,
          borderColor: hovered ? `${s.accent}99` : `${s.accent}55`,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Фоновое свечение */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `radial-gradient(ellipse at 10% 50%, ${s.accent}18 0%, transparent 60%)`,
        }} />
        <Particles color={s.accent} />

        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {/* Эмодзи с пульсом */}
          <div style={{
            flexShrink: 0, fontSize: "2rem",
            animation: "pulse-glow 2s ease-in-out infinite",
            filter: `drop-shadow(0 0 8px ${s.accent}88)`,
          }}>
            {banner.emoji}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {banner.tag && (
              <div style={{
                display: "inline-block",
                background: s.tag_bg, border: `1px solid ${s.accent}44`,
                borderRadius: "0.4rem", padding: "1px 8px",
                fontFamily: "'Fredoka One',sans-serif", fontSize: "0.65rem",
                color: s.tag_color, letterSpacing: "0.05em",
                marginBottom: 4,
              }}>
                {banner.tag}
              </div>
            )}
            <div style={{
              fontFamily: "'Fredoka One',sans-serif",
              fontSize: "0.95rem", color: "#fff",
              textShadow: "1px 1px 0 #000", lineHeight: 1.2,
            }}>
              {banner.title}
            </div>
            {banner.subtitle && (
              <div style={{
                fontFamily: "'Nunito',sans-serif", fontSize: "0.75rem",
                color: "#888", marginTop: 2, lineHeight: 1.4,
              }}>
                {banner.subtitle}
              </div>
            )}
          </div>

          {/* CTA кнопка */}
          {banner.cta_text && (
            <div style={{
              flexShrink: 0,
              background: s.accent,
              border: "2px solid #000",
              borderRadius: "0.65rem",
              padding: "0.4rem 0.75rem",
              fontFamily: "'Fredoka One',sans-serif",
              fontSize: "0.8rem", color: "#000",
              boxShadow: `3px 3px 0 #000`,
              whiteSpace: "nowrap",
              transition: "transform 0.15s, box-shadow 0.15s",
              transform: hovered ? "translateY(-1px)" : "none",
            }}>
              {banner.cta_text}
            </div>
          )}
        </div>

        <style>{`
          @keyframes pulse-glow {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.08); }
          }
        `}</style>
      </div>
    );
  }

  // ── Полная версия (десктоп лейдинг) ──────────────────────────────────────
  return (
    <section
      style={{
        position: "relative", overflow: "hidden",
        background: `linear-gradient(135deg, ${s.bg1} 0%, ${s.bg2} 60%, ${s.bg1} 100%)`,
        border: `2.5px solid ${hovered ? s.accent + "cc" : s.accent + "44"}`,
        borderRadius: "2rem",
        padding: "2.5rem 3rem",
        cursor: "pointer",
        margin: "3rem 0",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.6s ease, transform 0.6s ease, border-color 0.2s, box-shadow 0.2s",
        boxShadow: hovered
          ? `0 20px 80px ${s.glow}35, inset 0 1px 0 ${s.accent}33`
          : `0 8px 40px ${s.glow}18, inset 0 1px 0 ${s.accent}18`,
      }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Фоновые слои */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `radial-gradient(ellipse at 15% 50%, ${s.accent}20 0%, transparent 55%),
                     radial-gradient(ellipse at 85% 50%, ${s.accent2}15 0%, transparent 50%)`,
        transition: "opacity 0.3s",
        opacity: hovered ? 1 : 0.6,
      }} />

      {/* Горизонтальная полоса-акцент сверху */}
      <div style={{
        position: "absolute", top: 0, left: "10%", right: "10%", height: 2,
        background: `linear-gradient(90deg, transparent, ${s.accent}, ${s.accent2}, ${s.accent}, transparent)`,
        opacity: 0.8,
      }} />

      <Particles color={s.accent} />

      {/* Большая декоративная эмодзи справа */}
      <div style={{
        position: "absolute", right: "3rem", top: "50%",
        transform: "translateY(-50%)",
        fontSize: "5rem",
        opacity: 0.15,
        filter: `blur(2px) drop-shadow(0 0 30px ${s.accent})`,
        pointerEvents: "none",
        userSelect: "none",
      }}>
        {banner.emoji}
      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "2rem" }}>
        {/* Главная эмодзи */}
        <div style={{
          flexShrink: 0,
          width: 80, height: 80,
          background: `radial-gradient(circle, ${s.accent}33 0%, ${s.bg1} 70%)`,
          border: `2px solid ${s.accent}55`,
          borderRadius: "1.25rem",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "2.5rem",
          boxShadow: `0 0 30px ${s.glow}44`,
          transition: "transform 0.2s",
          transform: hovered ? "scale(1.05) rotate(-3deg)" : "scale(1) rotate(0deg)",
        }}>
          {banner.emoji}
        </div>

        {/* Текст */}
        <div style={{ flex: 1 }}>
          {banner.tag && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: s.tag_bg, border: `1.5px solid ${s.accent}44`,
              borderRadius: "0.5rem", padding: "3px 12px",
              fontFamily: "'Fredoka One',sans-serif", fontSize: "0.75rem",
              color: s.tag_color, letterSpacing: "0.08em",
              marginBottom: 10,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.tag_color, display: "inline-block", boxShadow: `0 0 6px ${s.tag_color}` }} />
              {banner.tag}
            </div>
          )}
          <div style={{
            fontFamily: "'Fredoka One',sans-serif",
            fontSize: "clamp(1.4rem, 2.5vw, 2rem)",
            color: "#fff",
            textShadow: `2px 2px 0 #000, 0 0 30px ${s.glow}44`,
            lineHeight: 1.2, marginBottom: 8,
          }}>
            {banner.title}
          </div>
          {banner.subtitle && (
            <div style={{
              fontFamily: "'Nunito',sans-serif",
              fontSize: "clamp(0.85rem, 1.2vw, 1rem)",
              color: "#999", lineHeight: 1.5, maxWidth: 500,
            }}>
              {banner.subtitle}
            </div>
          )}
        </div>

        {/* CTA кнопка */}
        {banner.cta_text && (
          <div style={{ flexShrink: 0 }}>
            <div style={{
              background: s.accent,
              border: "3px solid #000",
              borderRadius: "1rem",
              padding: "0.85rem 2rem",
              fontFamily: "'Fredoka One',sans-serif",
              fontSize: "1.1rem", color: "#000",
              boxShadow: `5px 5px 0 #000`,
              whiteSpace: "nowrap",
              transition: "transform 0.15s, box-shadow 0.15s",
              transform: hovered ? "translateY(-3px)" : "none",
              boxShadow: hovered ? `5px 8px 0 #000, 0 0 30px ${s.accent}55` : `5px 5px 0 #000`,
            }}>
              {banner.cta_text} →
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse-glow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </section>
  );
}